import qs from 'qs';
import stringUtils from '../../utils/string';
import typeUtils from '../../utils/type';
import {
	DEFAULT_TAKE,
	defaultPagination,
	LIST_DATA_TAKE_ITEMS_MARGIN,
	LIST_ITEM_DEFAULT_EMPTY_PLACEHOLDER,
} from './const';
import isEmpty from 'lodash/isEmpty';
import isObjectLike from 'lodash/isObjectLike';
import { supportsHistory } from '../../utils/browser';
import { CustomObject } from '../../types';
import { GeneralListColumn } from './types';
import { MultiSelectItem } from '../../components/MultiSelectProvider/types';
import { Bound } from '../../components/Range/const';
import { isNullOrUndefined, isUndefined } from '../../predicates/type';
import { ReactNode } from 'react';
import { browserHistory } from '@packages/router-dom';
import { isBadOrEmptyString } from '../../predicates/string';
import { removeDuplicatedKeysProps, stringifyPropsRecursively, transformKeysToLower } from '../../utils/object';

const EXCLUDE_FILTER_KEYS = ['__requestverificationtoken'];
const EXCLUDE_FILTER_VALUES = ['[]', 'undefined'];

const encodeDangerousFilterValues = (values: CustomObject): CustomObject => {
	const result = {};

	Object.keys(values).forEach((key) => {
		const value = values[key];

		if (typeof value === 'string' && value.length > 0) {
			result[key] = encodeURIComponent(value);
		} else {
			result[key] = value;
		}
	});

	return result;
};

export const sanitizeAndEncodeValues = (
	values: Parameters<typeof sanitizeValues>[0],
	params?: Parameters<typeof sanitizeValues>[1],
): CustomObject => {
	const normalizedFilterValues = sanitizeValues(values, params);

	return encodeDangerousFilterValues(normalizedFilterValues);
};

export const updateUrlWithQueryParams = (params = {}) => {
	if (!supportsHistory()) {
		return;
	}

	const filteredParams = normalizeValuesForUrlQueryParams(params);

	const queryParams = qs.stringify(filteredParams, {
		arrayFormat: 'comma',
		allowDots: true,
		encode: true,
	});

	const { pathname, search } = window.location;
	const pathWithSearch = queryParams.length > 0 ? `${pathname}?${queryParams}` : pathname;

	if (search.slice(1) !== queryParams) {
		browserHistory.replace(pathWithSearch, undefined, {
			scroll: false,
		});
	}
};

const EXCLUDE_KEYS_FOR_URL_QUERY_PARAMS = ['skip', 'take'];

export function normalizeValuesForUrlQueryParams(params = {}) {
	const newParams = {};

	Object.keys(params).forEach((paramKey) => {
		if (EXCLUDE_KEYS_FOR_URL_QUERY_PARAMS.includes(paramKey)) {
			return;
		}

		newParams[paramKey] = params[paramKey];
	});

	return newParams;
}

export const hasSavedFilterProp = (requestData) => {
	if (typeUtils.isNullOrUndefined(requestData)) {
		return false;
	}

	const lowerCaseRequestData = transformKeysToLower(requestData);

	return (
		!typeUtils.isNullOrUndefined(lowerCaseRequestData['savedfilter']) &&
		!isEmpty(lowerCaseRequestData['savedfilter'])
	);
};

export const hasSavedFilterId = (id) => {
	if (stringUtils.isNullOrEmpty(id)) {
		return false;
	}

	const savedFilterId = Number(id);

	return !Number.isNaN(savedFilterId) && savedFilterId !== 0;
};

function hasActiveSavedFilter(savedFilter: CustomObject) {
	const lowerCaseSavedFilter: {
		id?: number;
		isadvancedmode?: boolean;
	} = transformKeysToLower(savedFilter);
	return (
		hasSavedFilterId(lowerCaseSavedFilter.id) ||
		String(lowerCaseSavedFilter.isadvancedmode).toLowerCase() === 'true'
	);
}

export const shouldIncludeSavedFilterInRequestData = (requestData: CustomObject = {}) => {
	if (!hasSavedFilterProp(requestData)) {
		return false;
	}

	const loweredRequestData = transformKeysToLower(requestData) as {
		savedfilter: {
			id?: number;
			isadvancedmode?: boolean;
		};
	} & CustomObject;
	const savedFilter = loweredRequestData.savedfilter;

	return hasActiveSavedFilter(savedFilter);
};

/*
	Checks if the selected saved filter has changed between requests.
*/
export const hasSelectedSavedFilter = (values, previousValues, params?: { firstRequest?: boolean }) => {
	const lowerCaseValues: {
		savedfilter?: {
			id?: number;
		};
	} & CustomObject = transformKeysToLower(values);
	const lowerCasePreviousValues: {
		savedfilter?: {
			id?: number;
		};
	} & CustomObject = transformKeysToLower(previousValues);
	const savedFilterIdFromParams = Number(lowerCaseValues?.savedfilter?.id);
	const savedFilterId = Number.isNaN(savedFilterIdFromParams) ? 0 : savedFilterIdFromParams;
	const hasPreviousSavedFilterId =
		hasSavedFilterProp(previousValues) && hasSavedFilterId(lowerCasePreviousValues.savedfilter.id);

	// we should skip previous cached saved filter value if it's the first request
	if (!hasPreviousSavedFilterId || params?.firstRequest) {
		return savedFilterId !== 0;
	}

	const previousSavedFilterIdFromParams = Number(lowerCasePreviousValues.savedfilter.id);
	const previousSavedFilterId = Number.isNaN(previousSavedFilterIdFromParams) ? 0 : previousSavedFilterIdFromParams;

	return savedFilterId !== 0 && savedFilterId !== previousSavedFilterId;
};

export const getEncodedParamsForDetailPageLink = ({
	filterValues,
	listItemsIds,
}: {
	filterValues?: CustomObject;
	listItemsIds?: Array<string>;
} = {}) => {
	const param = {
		filter: filterValues,
		listItemsIds,
	};

	const filterValuesJson = JSON.stringify(param);

	return window.btoa(filterValuesJson);
};

export const normalizeListItemsForLink = ({ items, navigationItemIndex, take = defaultPagination.take }) => {
	const itemsCount = items && items.length;

	if (typeUtils.isNullOrUndefined(navigationItemIndex) || itemsCount === 0 || typeof window.btoa !== 'function') {
		return [];
	}

	take = Number(take);
	const isNegativeItemsIndex = 1 + navigationItemIndex - LIST_DATA_TAKE_ITEMS_MARGIN <= 0;
	const startSlicePos = isNegativeItemsIndex ? 0 : navigationItemIndex - LIST_DATA_TAKE_ITEMS_MARGIN;
	const noItemsOnTheStart = startSlicePos === 0;
	const endSlicePos = navigationItemIndex + LIST_DATA_TAKE_ITEMS_MARGIN + 1;
	const filteredListItemsIds = items.slice(startSlicePos, endSlicePos);
	const noItemsOnTheEnd = typeUtils.isNullOrUndefined(items[endSlicePos]) && itemsCount % take !== 0;

	if (noItemsOnTheStart) {
		filteredListItemsIds.unshift(null);
	}

	if (noItemsOnTheEnd) {
		filteredListItemsIds.push(null);
	}

	return filteredListItemsIds;
};

export const getListItemLink = ({
	items,
	navigationItemIndex,
	filterValues,
	take = defaultPagination.take,
}: {
	items?: Array<unknown>;
	navigationItemIndex?: string | number;
	filterValues?: CustomObject;
	take?: number;
}) => {
	return getEncodedParamsForDetailPageLink({
		filterValues,
		listItemsIds: normalizeListItemsForLink({
			items,
			navigationItemIndex,
			take,
		}),
	});
};

export const decodeListData = (listData) => {
	if (stringUtils.isNullOrEmpty(listData)) {
		return {};
	}

	try {
		listData = JSON.parse(decodeURIComponent(atob(listData)));
	} catch (e) {
		return {};
	}

	//here can be special decode behaviour in future...

	return listData;
};

export const decodeListDataFilter = (listData) => {
	const decodedListData = decodeListData(listData);

	return decodedListData.filter || {};
};

export const isVisibleColumn = (column: GeneralListColumn, columnsSettings: Array<MultiSelectItem> = []) => {
	if (column.isRequired) {
		return true;
	}

	const setting = columnsSettings.find((i) => i.name === column.name);

	return setting ? setting.visible : column.visible;
};

export const getVisibleColumns = (columns: Array<GeneralListColumn>, columnsSettings: Array<MultiSelectItem> = []) => {
	return columns.filter((column) => isVisibleColumn(column, columnsSettings));
};

export const getSortedColumnsByOrder = (columns) => {
	return columns.sort((a, b) => a.order - b.order);
};

export const getSortedAndVisibleColumns = (
	columns: Array<GeneralListColumn>,
	columnsSettings: Array<MultiSelectItem> = [],
): Array<GeneralListColumn> => {
	return getSortedColumnsByOrder(getVisibleColumns(columns, columnsSettings));
};

export const getFlattenObject = (obj) => {
	const flattenObj = {};

	for (const i in obj) {
		if (obj.hasOwnProperty(i)) {
			if (typeof obj[i] === 'object' && !Array.isArray(obj[i])) {
				const nestedObject = getFlattenObject(obj[i]);
				for (const j in nestedObject) {
					if (nestedObject.hasOwnProperty(j)) {
						flattenObj[`${i}.${j}`] = nestedObject[j];
					}
				}
			} else {
				flattenObj[i] = obj[i];
			}
		}
	}
	return flattenObj;
};

export const formatEmptyListNode = (listNode: ReactNode): ReactNode | typeof LIST_ITEM_DEFAULT_EMPTY_PLACEHOLDER => {
	if (isNullOrUndefined(listNode) || (typeof listNode === 'string' && listNode.trim() === '')) {
		return LIST_ITEM_DEFAULT_EMPTY_PLACEHOLDER;
	}

	return listNode;
};

function shouldExcludeBadValue(key: string, value: unknown) {
	const lowerCaseKey = String(key).toLowerCase();
	const stringedLowerCaseValue = String(value).toLowerCase();

	if (EXCLUDE_FILTER_KEYS.includes(lowerCaseKey) || EXCLUDE_FILTER_VALUES.includes(stringedLowerCaseValue)) {
		return true;
	}

	//boolean checkers
	if ((typeof value === 'string' && stringedLowerCaseValue === 'false') || (typeof value === 'boolean' && !value)) {
		return true;
	}

	if (typeof value === 'object' && (value.hasOwnProperty('min') || value.hasOwnProperty('max'))) {
		return isBadOrEmptyString(value['min']) && isBadOrEmptyString(value['max']);
	}

	if (lowerCaseKey === 'savedfilter' && !hasActiveSavedFilter(value as CustomObject)) {
		return true;
	}

	return false;
}

function sanitizeValues(
	filters: CustomObject,
	{
		forceIncludeKeys,
		skipKeys,
		skipPartialKeys,
		defaultTake,
		includePagination,
	}: {
		forceIncludeKeys?: string[];
		skipKeys?: string[];
		skipPartialKeys?: string[];
		defaultTake?: string | number;
		includePagination?: boolean;
	} = {},
) {
	const newFilters: CustomObject = {};
	const loweredCaseSkipKeys = isUndefined(skipKeys) ? [] : skipKeys.map((i) => i.toLowerCase());
	const loweredCaseSkipPartialKeys = isUndefined(skipPartialKeys) ? [] : skipPartialKeys.map((i) => i.toLowerCase());
	const lowerCaseForceIncludeKeys = isUndefined(forceIncludeKeys)
		? []
		: forceIncludeKeys.map((i) => String(i).toLowerCase());
	const filtersWithoutDuplications = removeDuplicatedKeysProps(filters);

	Object.keys(filtersWithoutDuplications).forEach((key) => {
		const value = filtersWithoutDuplications[key];
		const lowerCaseKey = String(key).toLowerCase();

		if (isBadOrEmptyString(value)) {
			return;
		}

		if (shouldExcludeBadValue(key, value) && !lowerCaseForceIncludeKeys.includes(lowerCaseKey)) {
			return;
		}

		const hasPartialKey = loweredCaseSkipPartialKeys.some((i) => lowerCaseKey.indexOf(i) !== -1);

		if (hasPartialKey) {
			return;
		}

		const hasAbandonedCacheKey = loweredCaseSkipKeys.some((i) => {
			return i === lowerCaseKey;
		});

		if (hasAbandonedCacheKey) {
			return;
		}

		if (lowerCaseKey === 'take' || lowerCaseKey === 'skip') {
			if (!includePagination || isUndefined(defaultTake)) {
				return;
			}

			if (lowerCaseKey === 'take' && String(defaultTake) === String(DEFAULT_TAKE)) {
				return;
			}

			if (lowerCaseKey === 'skip' && String(value) === '0') {
				return;
			}
		}

		newFilters[key] = value;
	});

	return newFilters;
}

function formatFilters(
	filters: Parameters<typeof sanitizeValues>[0],
	{
		isSelectedSavedFilter,
		forceIncludeKeys,
		includePagination,
		skipKeys,
		skipPartialKeys,
		defaultTake,
	}: Parameters<typeof sanitizeValues>[1] & {
		isSelectedSavedFilter?: boolean;
	} = {},
) {
	const lowerCaseFilters = transformKeysToLower(filters);

	if (!isUndefined(defaultTake) && defaultTake !== DEFAULT_TAKE && isUndefined(lowerCaseFilters['take'])) {
		filters['take'] = defaultTake;
	}

	const sanitizedFilters = sanitizeValues(filters, {
		forceIncludeKeys,
		skipKeys,
		skipPartialKeys,
		includePagination,
		defaultTake,
	});
	const newFilters: CustomObject = {};
	const formatSavedFilterProp = (savedFilter: CustomObject) => {
		const lowerCaseSavedFilter: {
			id?: number;
			isadvancedmode?: boolean;
			isvalueselected?: boolean;
		} = transformKeysToLower(savedFilter);

		if (isSelectedSavedFilter) {
			return {
				...lowerCaseSavedFilter,
				id: lowerCaseSavedFilter.id,
				isvalueselected: true,
			};
		}

		//eslint-disable-next-line
		const { isvalueselected, ...filteredSavedFilter } = lowerCaseSavedFilter;
		return {
			...filteredSavedFilter,
			id: lowerCaseSavedFilter.id,
		};
	};
	const formatSliderProp = (value: CustomObject) => {
		const slider: Partial<{
			min?: null | string;
			max?: null | string;
		}> = {};

		if (!stringUtils.isNullOrEmpty(value[Bound.Min])) {
			slider[Bound.Min] = String(value[Bound.Min]);
		}

		if (!stringUtils.isNullOrEmpty(value[Bound.Max])) {
			slider[Bound.Max] = String(value[Bound.Max]);
		}

		return slider;
	};

	Object.keys(sanitizedFilters).forEach((key) => {
		const value = sanitizedFilters[key];
		const lowerCaseKey = String(key).toLowerCase();
		const valueIsObjectLike = isObjectLike(value);
		const stringValue = valueIsObjectLike ? transformKeysToLower(value) : String(value);

		if (lowerCaseKey === 'savedfilter') {
			if (shouldIncludeSavedFilterInRequestData(sanitizedFilters)) {
				newFilters[key] = formatSavedFilterProp(value as CustomObject);
			}

			return;
		}

		//slider values
		if (valueIsObjectLike && (!isUndefined(value['min']) || !isUndefined(value['max']))) {
			newFilters[key] = formatSliderProp(value as CustomObject);

			return;
		}

		if (typeof value === 'boolean' || typeof value === 'number') {
			newFilters[key] = stringValue;

			return;
		}

		if (typeof value === 'string') {
			if (value.indexOf(',') === -1) {
				newFilters[key] = value;
			} else {
				newFilters[key] = value.split(',').map((i) => String(i));
			}

			return;
		}

		if (valueIsObjectLike) {
			const transformedObj = Array.isArray(value) ? value : transformKeysToLower(value as CustomObject);

			newFilters[key] = stringifyPropsRecursively(transformedObj);

			return;
		}

		newFilters[key] = value;
	});

	return newFilters;
}

export { sanitizeValues, formatFilters, EXCLUDE_FILTER_KEYS, EXCLUDE_FILTER_VALUES };
